import Layout from "@/components/layout/Layout";
import React, { useState } from "react";
import Sidepanel from "@/components/layout/Sidepanel";
import Content from "@/components/layout/Content";
import ContentOptions from "@/components/layout/ContentOptions";
import Heading from "@/components/elements/Heading";
import NavigationButtons from "@/components/elements/Buttons";
import Label from "@/components/elements/Label";
import Dropdown from "@/components/elements/Dropdown";
import NumberInput from "@/components/elements/NumberInput";
import Drawing from "@/components/drawing/drawing";
import api from "@/services/api";

/**
 * FinishingSide (Individual Side Finishing) Configuration Component
 *
 * Handles the configuration of finishing options for individual sides of the product
 * when different sides require different groove/edge finishing specifications.
 * Allows users to configure specific side preferences and dimensions.
 *
 * @param {Object} props - Component props
 * @param {Object} props.session - Current session information
 * @param {string} props.shoppingurl - URL for shopping cart navigation
 * @param {string} props.step - Current configuration step identifier
 * @param {Array} props.steps - Available configuration steps
 * @param {Object} props.product - Product configuration data
 * @param {Object} props.product.properties - Current product properties
 * @param {string} props.heading - Page heading text
 * @param {string} props.subheading - Page subheading text
 * @param {string} props.saveUrl - API endpoint for saving configuration
 * @param {string} props.prevLink - URL for previous step navigation
 * @param {string} props.nextLink - URL for next step navigation
 * @param {string} props.randname - Side identifier (e.g., "LI", "RE", "BO", "O")
 * @param {string} props.sponningside - Property name for this side's finishing preference
 * @param {Array} props.colors - Available color options for visualization
 *
 * @returns {JSX.Element} The rendered individual side finishing configuration page
 */
export default function FinishingSide({
    session,
    shoppingurl,
    step,
    steps,
    product,
    heading,
    subheading,
    saveUrl,
    prevLink,
    nextLink,
    randname,
    sponningside,
    colors,
}) {
    // Initialize state with current product data
    const [productprops, setProductprops] = useState(product);
    const [properties, setProperties] = useState(product.properties);
    const [stepList, setStepList] = useState(steps);

    /**
     * Update properties with optional server persistence
     * Handles property updates and conditionally saves to server
     *
     * @param {Object} properties - Updated properties object
     * @param {boolean} doRequest - Whether to save to server (default: true)
     */
    function updateProps(properties, doRequest = true) {
        setProperties(properties);
        if (doRequest) {
            api.put(saveUrl, { properties: properties });
        }
    }

    /**
     * Handle side finishing preference changes with property updates
     * When the finishing side preference changes for this specific side,
     * clears all groove settings and applies the new preference with current dimensions
     *
     * @param {Object} newvalue - Updated properties object
     * @param {string} property - Property being updated (side finishing preference)
     */
    function updateQuestion(newvalue, property) {
        let props = { ...newvalue };
        let value = props[property];

        // Get current dimension variable names for this side
        let breedtevar = getVariable(randname + "_BR"); // Width variable
        let hoogtevar = getVariable(randname + "_DI");  // Height variable
        let breedtesize = props[breedtevar];            // Current width value
        let hoogtesize = props[hoogtevar];              // Current height value

        // Clear all groove settings for this side (both inside and outside)
        props["SP_BU_" + randname + "_BR"] = 0;  // Outside width
        props["SP_BU_" + randname + "_DI"] = 0;  // Outside height
        props["SP_BI_" + randname + "_BR"] = 0;  // Inside width
        props["SP_BI_" + randname + "_DI"] = 0;  // Inside height

        // Apply the selected preference with current dimensions
        props[value + "_" + randname + "_BR"] = breedtesize;
        props[value + "_" + randname + "_DI"] = hoogtesize;

        setProperties(props);

        // Save to server and refresh product data
        api.put(saveUrl, { properties: props }).then((response) => {
            setProductprops(response.data);
        });
    }

    /**
     * Generate property variable name for groove dimensions
     * Combines the current side finishing preference with the specific dimension type
     *
     * @param {string} variable - The dimension type (e.g., "LI_BR", "RE_DI")
     * @returns {string} Complete property name based on current side preference
     */
    const getVariable = (variable) => {
        return properties[sponningside] + "_" + variable;
    };

    return (
        <Layout>
            <Sidepanel
                steps={stepList}
                shoppingurl={shoppingurl}
                session={session}
                product={product}
                step={step}
            />
            <Content
                steps={stepList}
                step={step}
                shoppingurl={shoppingurl}
                session={session}
                product={product}
            >
                <ContentOptions>
                    <Heading heading={heading} subheading={subheading} />

                    <div className="question">
                        <div className="d-block">
                            <Label label="Keuze zijdes" />
                            <Dropdown
                                properties={properties}
                                property={sponningside}
                                updateProperties={updateQuestion}
                                options={[
                                    { name: "Buiten", value: "SP_BU" },
                                    { name: "Binnen", value: "SP_BI" },
                                ]}
                            />
                        </div>
                        <div className="d-block">
                            <Label label="Sponningmaat breedte" />
                            <NumberInput
                                key={getVariable(randname + "_BR")}
                                properties={properties}
                                property={getVariable(randname + "_BR")}
                                field={{ min: 1, max: 1000000, unit: "mm" }}
                                updateProperties={updateProps}
                            />
                        </div>
                        <div className="d-block">
                            <Label label="Sponningmaat hoogte" />
                            <NumberInput
                                key={getVariable(randname + "_DI")}
                                properties={properties}
                                property={getVariable(randname + "_DI")}
                                field={{ min: 1, max: 1000000, unit: "mm" }}
                                updateProperties={updateProps}
                            />
                            <p style={{ fontSize: 12, marginTop: 10 }}>
                                De tekening is bij niet gelijke
                                sponning/randafwerking afwijkend.
                            </p>
                        </div>
                    </div>

                    <NavigationButtons
                        prevlink={prevLink}
                        nextlink={nextLink}
                    />
                </ContentOptions>
                <Drawing
                    session_product={productprops}
                    properties={properties}
                    products={product.products}
                    sponningside={properties[sponningside]}
                    colors={colors}
                    afwerkingactive
                />
            </Content>
        </Layout>
    );
}
